// components/views/VendorLibraryView.jsx — Enhanced Vendor Profile
import { useState, useEffect } from 'react';
import { Field, EmptyState, SectionLabel } from '../UI';
import supabase from '../../utils/supabaseClient';

// ── Date formatter ───────────────────────────────────────────────────────────
function fmtDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00'); // noon avoids timezone shift
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

// ── Expiration badge ──────────────────────────────────────────────────────────
function daysTil(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  return diff;
}
function ExpirationBadge({ date, label }) {
  const d = daysTil(date);
  if (d === null) return null;
  const color = d < 0 ? 'var(--red-600)' : d <= 30 ? 'var(--orange-600)' : 'var(--green-600)';
  const bg    = d < 0 ? 'var(--red-50)'  : d <= 30 ? 'var(--orange-50)'  : 'var(--green-50)';
  const text  = d < 0 ? `${label} EXPIRED` : d === 0 ? `${label} expires today` : `${label} exp. ${fmtDate(date)}`;
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: bg, color, border: '1px solid currentColor' }}>{text}</span>;
}

// ── Business types ────────────────────────────────────────────────────────────
const BUSINESS_TYPES = [
  'Sole Proprietorship', 'Partnership', 'LLC', 'Corporation (C-Corp)',
  'Corporation (S-Corp)', 'Nonprofit', 'Government Entity', 'Other',
];

// ── Document types ────────────────────────────────────────────────────────────
const DOC_TYPES = [
  { key: 'std204',    label: 'STD 204',                   icon: '📄', hasExpiry: false },
  { key: 'w9',        label: 'W-9',                       icon: '📋', hasExpiry: false },
  { key: 'sb_cert',   label: 'SB Verification Certificate', icon: '✅', hasExpiry: true  },
  { key: 'dvbe_cert', label: 'DVBE Verification Certificate', icon: '✅', hasExpiry: true },
  { key: 'license',   label: 'Business License',          icon: '🏛', hasExpiry: true  },
  { key: 'gl',        label: 'GL Insurance',              icon: '🛡', hasExpiry: true  },
  { key: 'wc',        label: "Workers' Comp Insurance",   icon: '🛡', hasExpiry: true  },
  { key: 'auto',      label: 'Auto / Commercial Vehicle Insurance', icon: '🚗', hasExpiry: true  },
  { key: 'eo',        label: 'E&O / Professional Liability', icon: '🛡', hasExpiry: true  },
  { key: 'bond',      label: 'Surety Bond',               icon: '📑', hasExpiry: true  },
  { key: 'other',     label: 'Other',                     icon: '📎', hasExpiry: false },
];

const EMPTY_VENDOR = {
  company_name: '', dba: '', business_type: '', website: '',
  phone: '', email: '', tax_id: '',
  poc_name: '', poc_title: '', poc_phone: '', poc_email: '',
  sb_certified: false, sb_cert_number: '', sb_expiration: '',
  dvbe_certified: false, dvbe_cert_number: '', dvbe_expiration: '',
  sam_registered: false, sam_expiration: '',
  caleprocure_id: '', fiscal_id: '',
  naics_codes: [],
  ownership_flags: { minority: false, women: false, veteran: false, hub_zone: false },
  addresses: [{ label: 'Main Office', street: '', city: '', state: 'CA', zip: '' }],
  notes: '', active: true,
  docs: {}, doc_metadata: {},
};

// ── Document Uploader with Penny extraction ───────────────────────────────────
function VendorDocUploader({ vendorId, docs, docMeta, onUpdate, onMetaUpdate, onProfileUpdate, currentProfile, apiKey, callAI, toast }) {
  const [uploading, setUploading] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);

  async function handleUpload(docType, file) {
    if (!file) return;
    setUploading(docType);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `vendors/${vendorId}/${docType}_${Date.now()}_${safeName}`;
      console.log('Uploading to vendor-files:', path);
      const { data: uploadData, error } = await supabase.storage.from('vendor-files').upload(path, file, { upsert: true });
      if (error) { console.error('Upload error:', error); throw error; }
      console.log('Upload success:', uploadData);
      const { data: { publicUrl } } = supabase.storage.from('vendor-files').getPublicUrl(path);
      const updated = { ...docs, [docType]: { name: file.name, path, url: publicUrl, uploadedAt: new Date().toISOString() } };
      onUpdate(updated);
      await supabase.from('vendor_library').update({ docs: updated }).eq('id', vendorId);
      toast.success(file.name + ' uploaded');

      // Auto-analyze with Penny — extract metadata AND profile fields
      const dt = DOC_TYPES.find(d => d.key === docType);
      if (apiKey && callAI) {
        setAnalyzing(docType);
        try {
          const reader = new FileReader();
          reader.onload = async () => {
            const base64 = reader.result.split(',')[1];
            const raw = await callAI(apiKey, {
              system: `You are Penny analyzing a vendor compliance document. Extract ALL available information and return ONLY valid JSON:
{
  "expiration_date": "YYYY-MM-DD or null",
  "effective_date": "YYYY-MM-DD or null",
  "policy_number": "string or null",
  "coverage_amount": "string or null",
  "insured_name": "string or null",
  "issuer": "string or null",
  "doc_type_detected": "string describing what this document is",
  "notes": "important flags or gaps the analyst should know (1-2 sentences max)",
  "confidence": "high|medium|low",
  "profile_fields": {
    "tax_id": "Federal Tax ID / EIN (XX-XXXXXXX format) if found, else null",
    "company_name": "Legal company name if clearly stated, else null",
    "phone": "Company phone number if found, else null",
    "email": "Company email if found, else null",
    "sb_cert_number": "SB certification number if this is an SB certificate, else null",
    "sb_expiration": "YYYY-MM-DD SB cert expiration if found, else null",
    "dvbe_cert_number": "DVBE certification number if this is a DVBE certificate, else null",
    "dvbe_expiration": "YYYY-MM-DD DVBE cert expiration if found, else null",
    "address_street": "Street address if found, else null",
    "address_city": "City if found, else null",
    "address_state": "State if found, else null",
    "address_zip": "ZIP code if found, else null"
  }
}
Return ONLY the JSON object. For profile_fields, only include values you are confident about.`,
              messages: [{
                role: 'user',
                content: [
                  { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } },
                  { type: 'text', text: `Extract all information from this ${dt.label} document.` }
                ]
              }],
              max_tokens: 600,
            });
            try {
              const parsed = JSON.parse(raw.replace(/```json|```/g, '').trim());

              // Save doc metadata
              const updatedMeta = { ...docMeta, [docType]: {
                expiration_date: parsed.expiration_date,
                effective_date: parsed.effective_date,
                policy_number: parsed.policy_number,
                coverage_amount: parsed.coverage_amount,
                insured_name: parsed.insured_name,
                issuer: parsed.issuer,
                doc_type_detected: parsed.doc_type_detected,
                notes: parsed.notes,
                confidence: parsed.confidence,
                analyzedAt: new Date().toISOString(),
              }};
              onMetaUpdate(updatedMeta);

              // Map profile fields — only populate if currently empty
              const pf = parsed.profile_fields || {};
              const profileUpdates = {};
              const populated = [];

              if (pf.tax_id && !currentProfile.tax_id) { profileUpdates.tax_id = pf.tax_id; populated.push('Tax ID'); }
              if (pf.company_name && !currentProfile.company_name) { profileUpdates.company_name = pf.company_name; populated.push('Company Name'); }
              if (pf.phone && !currentProfile.phone) { profileUpdates.phone = pf.phone; populated.push('Phone'); }
              if (pf.email && !currentProfile.email) { profileUpdates.email = pf.email; populated.push('Email'); }
              if (pf.sb_cert_number && !currentProfile.sb_cert_number) { profileUpdates.sb_cert_number = pf.sb_cert_number; profileUpdates.sb_certified = true; populated.push('SB Cert #'); }
              if (pf.sb_expiration && !currentProfile.sb_expiration) { profileUpdates.sb_expiration = pf.sb_expiration; populated.push('SB Expiration'); }
              if (pf.dvbe_cert_number && !currentProfile.dvbe_cert_number) { profileUpdates.dvbe_cert_number = pf.dvbe_cert_number; profileUpdates.dvbe_certified = true; populated.push('DVBE Cert #'); }
              if (pf.dvbe_expiration && !currentProfile.dvbe_expiration) { profileUpdates.dvbe_expiration = pf.dvbe_expiration; populated.push('DVBE Expiration'); }

              // Address — only if no addresses set
              const hasAddress = currentProfile.addresses?.some(a => a.street);
              if (!hasAddress && pf.address_street) {
                profileUpdates.addresses = [{ label: 'Main Office', street: pf.address_street, city: pf.address_city || '', state: pf.address_state || 'CA', zip: pf.address_zip || '' }];
                populated.push('Address');
              }

              // Save everything in one update
              const dbUpdate = { doc_metadata: updatedMeta };
              if (Object.keys(profileUpdates).length > 0) {
                Object.assign(dbUpdate, profileUpdates);
                onProfileUpdate(profileUpdates);
              }
              await supabase.from('vendor_library').update(dbUpdate).eq('id', vendorId);

              if (populated.length > 0) {
                toast.success(`✦ Penny populated: ${populated.join(', ')}`);
              } else {
                toast.success('✦ Penny extracted document details');
              }
            } catch (e) { console.warn('Penny doc parse error:', e); toast.success('✦ Document analyzed'); }
          };
          reader.readAsDataURL(file);
        } catch (e) { console.warn('Penny doc analysis failed:', e); }
        finally { setAnalyzing(null); }
      }
    } catch (e) { toast.error('Upload failed: ' + e.message); setUploading(null); }
    finally { setUploading(null); }
  }

  async function handleDelete(docType) {
    try {
      const doc = docs[docType];
      if (doc?.path) await supabase.storage.from('vendor-files').remove([doc.path]);
      const updatedDocs = { ...docs };
      delete updatedDocs[docType];
      const updatedMeta = { ...docMeta };
      delete updatedMeta[docType];
      onUpdate(updatedDocs);
      onMetaUpdate(updatedMeta);
      await supabase.from('vendor_library').update({ docs: updatedDocs, doc_metadata: updatedMeta }).eq('id', vendorId);
      toast.success('Document removed');
    } catch (e) { toast.error('Remove failed: ' + e.message); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {DOC_TYPES.map(dt => {
        const doc = docs[dt.key];
        const meta = docMeta[dt.key];
        const isUploading = uploading === dt.key;
        const isAnalyzing = analyzing === dt.key;
        return (
          <div key={dt.key} style={{ borderRadius: 'var(--r-md)', border: '1px solid ' + (doc ? 'var(--teal-200)' : 'var(--slate-200)'), overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: doc ? 'var(--teal-50)' : 'var(--slate-50)' }}>
              <span style={{ fontSize: 18 }}>{dt.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-800)' }}>{dt.label}</div>
                {doc && <div style={{ fontSize: 11, color: 'var(--teal-600)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name}</div>}
                {!doc && <div style={{ fontSize: 11, color: 'var(--slate-400)' }}>No file uploaded</div>}
                {isAnalyzing && <div style={{ fontSize: 11, color: 'var(--gold-600)' }}>✦ Penny is reading this document…</div>}
              </div>
              {doc && (
              <button className="btn btn-ghost btn-xs" onClick={async () => {
                try {
                  // Try signed URL first
                  const { data: signedData, error: signedErr } = await supabase.storage
                    .from('vendor-files').createSignedUrl(doc.path, 300);
                  if (!signedErr && signedData?.signedUrl) {
                    window.open(signedData.signedUrl, '_blank');
                    return;
                  }
                  // Fallback: download as blob
                  const { data: blob, error: dlErr } = await supabase.storage
                    .from('vendor-files').download(doc.path);
                  if (dlErr) throw dlErr;
                  const url = URL.createObjectURL(blob);
                  window.open(url, '_blank');
                  setTimeout(() => URL.revokeObjectURL(url), 10000);
                } catch (e) {
                  console.error('View error:', e);
                  toast.error('Could not open file: ' + e.message);
                }
              }}>View</button>
            )}
              <label style={{ cursor: 'pointer' }}>
                <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => handleUpload(dt.key, e.target.files[0])} />
                <span className="btn btn-ghost btn-xs">{isUploading ? '…' : doc ? 'Replace' : 'Upload'}</span>
              </label>
              {doc && <button className="btn btn-ghost btn-xs" style={{ color: 'var(--red-400)' }} onClick={() => handleDelete(dt.key)}>✕</button>}
            </div>
            {/* Penny extracted metadata — editable */}
            {meta && (
              <div style={{ padding: '10px 12px', background: '#fff', borderTop: '1px solid var(--teal-100)' }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-400)', marginBottom: 8 }}>✦ Penny Extracted — Edit to correct</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                  {dt.hasExpiry && (
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--slate-500)', marginBottom: 3 }}>
                        Expiration Date
                        {meta.expiration_date && daysTil(meta.expiration_date) < 0 && <span style={{ color: 'var(--red-600)', marginLeft: 4 }}>⚠ EXPIRED</span>}
                        {meta.expiration_date && daysTil(meta.expiration_date) >= 0 && daysTil(meta.expiration_date) <= 30 && <span style={{ color: 'var(--orange-600)', marginLeft: 4 }}>⚠ Expiring soon</span>}
                      </div>
                      <input type="date" value={meta.expiration_date || ''}
                        onChange={async e => {
                          const updated = { ...docMeta, [dt.key]: { ...meta, expiration_date: e.target.value } };
                          onMetaUpdate(updated);
                          await supabase.from('vendor_library').update({ doc_metadata: updated }).eq('id', vendorId);
                        }}
                        style={{ width: '100%', padding: '5px 8px', border: '1.5px solid ' + (meta.expiration_date && daysTil(meta.expiration_date) < 0 ? 'var(--red-300)' : meta.expiration_date && daysTil(meta.expiration_date) <= 30 ? 'var(--orange-300)' : 'var(--slate-200)'), borderRadius: 'var(--r-md)', fontSize: 12, fontFamily: 'inherit' }} />
                    </div>
                  )}
                  {meta.policy_number !== undefined && (
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--slate-500)', marginBottom: 3 }}>Policy / Cert Number</div>
                      <input value={meta.policy_number || ''}
                        onChange={async e => {
                          const updated = { ...docMeta, [dt.key]: { ...meta, policy_number: e.target.value } };
                          onMetaUpdate(updated);
                          await supabase.from('vendor_library').update({ doc_metadata: updated }).eq('id', vendorId);
                        }}
                        style={{ width: '100%', padding: '5px 8px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12, fontFamily: 'monospace' }} />
                    </div>
                  )}
                  {meta.coverage_amount !== undefined && (
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--slate-500)', marginBottom: 3 }}>Coverage Amount</div>
                      <input value={meta.coverage_amount || ''}
                        onChange={async e => {
                          const updated = { ...docMeta, [dt.key]: { ...meta, coverage_amount: e.target.value } };
                          onMetaUpdate(updated);
                          await supabase.from('vendor_library').update({ doc_metadata: updated }).eq('id', vendorId);
                        }}
                        style={{ width: '100%', padding: '5px 8px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12, fontFamily: 'inherit' }} />
                    </div>
                  )}
                  {meta.issuer !== undefined && (
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--slate-500)', marginBottom: 3 }}>Issuer</div>
                      <input value={meta.issuer || ''}
                        onChange={async e => {
                          const updated = { ...docMeta, [dt.key]: { ...meta, issuer: e.target.value } };
                          onMetaUpdate(updated);
                          await supabase.from('vendor_library').update({ doc_metadata: updated }).eq('id', vendorId);
                        }}
                        style={{ width: '100%', padding: '5px 8px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12, fontFamily: 'inherit' }} />
                    </div>
                  )}
                </div>
                {meta.notes && (
                  <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--orange-600)' }}>⚠ {meta.notes}</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function VendorLibraryView({ apiKey, callAI, profile, toast }) {
  const [vendors, setVendors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState(EMPTY_VENDOR);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCert, setFilterCert] = useState('all');
  const [showAddressDrop, setShowAddressDrop] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [naicInput, setNaicInput] = useState('');

  function upd(k, v) { setForm(p => ({ ...p, [k]: v })); setDirty(true); }

  useEffect(() => { loadVendors(); }, []);

  async function loadVendors() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: prof } = await supabase.from('user_profiles').select('agency_id').eq('id', user.id).single();
      let q = supabase.from('vendor_library').select('*').order('company_name');
      if (prof?.agency_id) q = q.eq('agency_id', prof.agency_id);
      const { data, error } = await q;
      if (error) throw error;
      if (data) setVendors(data.map(normalizeVendor));
    } catch (e) { console.warn('loadVendors error:', e.message); }
  }

  function normalizeVendor(v) {
    return {
      ...EMPTY_VENDOR, ...v,
      sb_expiration:   v.sb_expiration?.split('T')[0]   || '',
      dvbe_expiration: v.dvbe_expiration?.split('T')[0] || '',
      sam_expiration:  v.sam_expiration?.split('T')[0]  || '',
      addresses: v.addresses?.length ? v.addresses
        : (v.address ? [{ label: 'Main Office', street: v.address, city: v.city || '', state: v.state || 'CA', zip: v.zip || '' }] : [{ label: 'Main Office', street: '', city: '', state: 'CA', zip: '' }]),
      naics_codes:     v.naics_codes || [],
      ownership_flags: v.ownership_flags || { minority: false, women: false, veteran: false, hub_zone: false },
      docs:            v.docs || {},
      doc_metadata:    v.doc_metadata || {},
    };
  }

  function selectVendor(v) {
    setSelected(v);
    setForm(normalizeVendor(v));
    setIsNew(false);
    setDirty(false);
    setLastSaved(null);
  }

  function startNew() {
    setSelected(null);
    setForm({ ...EMPTY_VENDOR });
    setIsNew(true);
    setDirty(false);
    setLastSaved(null);
  }

  async function save() {
    if (!form.company_name.trim()) { toast.warn('Company name is required'); return; }
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: prof } = await supabase.from('user_profiles').select('agency_id').eq('id', user.id).single();
      const payload = {
        agency_id:       prof?.agency_id,
        company_name:    form.company_name.trim(),
        dba:             form.dba || null,
        business_type:   form.business_type || null,
        website:         form.website || null,
        phone:           form.phone || null,
        email:           form.email || null,
        tax_id:          form.tax_id || null,
        poc_name:        form.poc_name || null,
        poc_title:       form.poc_title || null,
        poc_phone:       form.poc_phone || null,
        poc_email:       form.poc_email || null,
        sb_certified:    form.sb_certified,
        sb_cert_number:  form.sb_cert_number || null,
        sb_expiration:   form.sb_expiration || null,
        dvbe_certified:  form.dvbe_certified,
        dvbe_cert_number: form.dvbe_cert_number || null,
        dvbe_expiration: form.dvbe_expiration || null,
        sam_registered:  form.sam_registered,
        sam_expiration:  form.sam_expiration || null,
        caleprocure_id:  form.caleprocure_id || null,
        fiscal_id:       form.fiscal_id || null,
        naics_codes:     form.naics_codes || [],
        ownership_flags: form.ownership_flags || {},
        addresses:       form.addresses || [],
        notes:           form.notes || null,
        active:          form.active !== false,
        docs:            form.docs || {},
        doc_metadata:    form.doc_metadata || {},
        vendor_type:     'agency_managed',
      };
      if (isNew) {
        const { data, error } = await supabase.from('vendor_library').insert(payload).select().single();
        if (error) throw error;
        const normalized = normalizeVendor(data);
        setVendors(p => [...p, normalized].sort((a, b) => a.company_name.localeCompare(b.company_name)));
        setSelected(normalized); setForm(normalized); setIsNew(false);
        toast.success(form.company_name + ' added to vendor library');
        setDirty(false); setLastSaved(new Date());
      } else {
        const { error } = await supabase.from('vendor_library').update(payload).eq('id', selected.id);
        if (error) throw error;
        const updated = normalizeVendor({ ...selected, ...payload });
        setVendors(p => p.map(v => v.id === selected.id ? updated : v));
        setSelected(updated); setForm(updated);
        toast.success('Vendor profile saved');
        setDirty(false); setLastSaved(new Date());
      }
    } catch (e) { toast.error('Save failed: ' + e.message); }
    finally { setSaving(false); }
  }

  async function deactivate(id) {
    await supabase.from('vendor_library').update({ active: false }).eq('id', id);
    setVendors(p => p.map(v => v.id === id ? { ...v, active: false } : v));
    if (selected?.id === id) setSelected(null);
    toast.info('Vendor deactivated');
  }

  // Expiring certs warning
  const expiring = vendors.filter(v => {
    const d1 = daysTil(v.sb_expiration), d2 = daysTil(v.dvbe_expiration), d3 = daysTil(v.sam_expiration);
    const docExpiries = Object.values(v.doc_metadata || {}).map(m => daysTil(m.expiration_date)).filter(d => d !== null);
    return [d1, d2, d3, ...docExpiries].some(d => d !== null && d <= 30);
  });

  const filtered = vendors.filter(v => {
    if (!v.active) return false;
    const q = search.toLowerCase();
    const matchSearch = !q || v.company_name.toLowerCase().includes(q) || (v.dba || '').toLowerCase().includes(q) || (v.email || '').toLowerCase().includes(q);
    const matchCert = filterCert === 'all' || (filterCert === 'sb' && v.sb_certified) || (filterCert === 'dvbe' && v.dvbe_certified) || (filterCert === 'both' && v.sb_certified && v.dvbe_certified);
    return matchSearch && matchCert;
  });

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      {/* ── Vendor List ── */}
      <div style={{ width: 300, flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendors…"
            style={{ flex: 1, padding: '8px 12px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 13, fontFamily: 'inherit' }} />
          <button className="btn btn-gold btn-sm" onClick={startNew}>+ Add</button>
        </div>

        {/* Cert filter */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {[['all','All'],['sb','SB'],['dvbe','DVBE'],['both','SB+DVBE']].map(([v,l]) => (
            <button key={v} onClick={() => setFilterCert(v)}
              style={{ fontSize: 11, padding: '3px 10px', borderRadius: 99, border: '1px solid', borderColor: filterCert === v ? 'var(--navy-600)' : 'var(--slate-200)', background: filterCert === v ? 'var(--navy-600)' : '#fff', color: filterCert === v ? '#fff' : 'var(--slate-600)', cursor: 'pointer', fontWeight: filterCert === v ? 700 : 400 }}>
              {l}
            </button>
          ))}
        </div>

        {expiring.length > 0 && (
          <div style={{ padding: '8px 12px', background: 'var(--orange-50)', border: '1px solid var(--orange-200)', borderRadius: 'var(--r-md)', fontSize: 12, color: 'var(--orange-700)', marginBottom: 12 }}>
            ⚠ {expiring.length} vendor{expiring.length !== 1 ? 's have' : ' has'} docs or certs expiring within 30 days
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filtered.length === 0 && <EmptyState message="No vendors found" />}
          {filtered.map(v => {
            const hasExpiring = [daysTil(v.sb_expiration), daysTil(v.dvbe_expiration), daysTil(v.sam_expiration),
              ...Object.values(v.doc_metadata || {}).map(m => daysTil(m.expiration_date))
            ].some(d => d !== null && d <= 30);
            return (
              <div key={v.id} onClick={() => selectVendor(v)}
                style={{ padding: '10px 14px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (selected?.id === v.id ? 'var(--gold-400)' : 'var(--slate-200)'), background: selected?.id === v.id ? 'var(--gold-50)' : '#fff', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-800)' }}>{v.company_name}</div>
                  {hasExpiring && <span style={{ fontSize: 10, color: 'var(--orange-600)' }}>⚠</span>}
                </div>
                {v.dba && <div style={{ fontSize: 11, color: 'var(--slate-400)' }}>dba {v.dba}</div>}
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                  {v.sb_certified && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: 'var(--teal-50)', color: 'var(--teal-700)', border: '1px solid var(--teal-200)' }}>SB</span>}
                  {v.dvbe_certified && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: 'var(--blue-50)', color: 'var(--blue-700)', border: '1px solid var(--blue-200)' }}>DVBE</span>}
                  {v.sam_registered && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: 'var(--slate-100)', color: 'var(--slate-600)', border: '1px solid var(--slate-200)' }}>SAM</span>}
                  <ExpirationBadge date={v.sb_expiration} label="SB" />
                  <ExpirationBadge date={v.dvbe_expiration} label="DVBE" />
                </div>
                <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 2 }}>{v.email || ''}{v.addresses?.[0]?.city ? ' · ' + v.addresses[0].city : ''}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Vendor Form ── */}
      {(selected || isNew) && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Company Info */}
          <div className="card">
            <h3 className="card-title">Company Information</h3>
            <div className="form-grid">
              <Field label="Legal Company Name *" value={form.company_name} onChange={v => upd('company_name', v)} className="span-full" required />
              <Field label="DBA (if applicable)" value={form.dba || ''} onChange={v => upd('dba', v)} />
              <div className="form-field">
                <label>Business Type</label>
                <select value={form.business_type} onChange={e => upd('business_type', e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}>
                  <option value="">Select…</option>
                  {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <Field label="Phone" value={form.phone || ''} onChange={v => upd('phone', v)} />
              <Field label="Email" value={form.email || ''} onChange={v => upd('email', v)} />
              <Field label="Federal Tax ID" value={form.tax_id || ''} onChange={v => upd('tax_id', v)} monospace placeholder="XX-XXXXXXX" />
              <Field label="Website" value={form.website || ''} onChange={v => upd('website', v)} placeholder="https://" className="span-full" />
            </div>

            {/* Ownership flags */}
            <div style={{ marginTop: 14 }}>
              <SectionLabel>Ownership</SectionLabel>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {[['minority','Minority-Owned'],['women','Women-Owned'],['veteran','Veteran-Owned'],['hub_zone','HUBZone']].map(([k, l]) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!form.ownership_flags?.[k]} onChange={e => upd('ownership_flags', { ...form.ownership_flags, [k]: e.target.checked })} />
                    {l}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Addresses */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 className="card-title" style={{ margin: 0 }}>Addresses</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => upd('addresses', [...(form.addresses || []), { label: '', street: '', city: '', state: 'CA', zip: '' }])}>+ Add Address</button>
            </div>
            {(form.addresses || []).map((addr, i) => (
              <div key={i} style={{ marginBottom: 12, padding: '12px 14px', borderRadius: 'var(--r-md)', border: '1px solid var(--slate-200)', background: 'var(--slate-50)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <Field label="Label" value={addr.label || ''} placeholder="e.g., Main Office, Billing"
                    onChange={v => upd('addresses', form.addresses.map((a, idx) => idx === i ? { ...a, label: v } : a))} />
                  <Field label="Street" value={addr.street || ''}
                    onChange={v => upd('addresses', form.addresses.map((a, idx) => idx === i ? { ...a, street: v } : a))} />
                  <Field label="City" value={addr.city || ''}
                    onChange={v => upd('addresses', form.addresses.map((a, idx) => idx === i ? { ...a, city: v } : a))} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Field label="State" value={addr.state || 'CA'}
                      onChange={v => upd('addresses', form.addresses.map((a, idx) => idx === i ? { ...a, state: v } : a))} />
                    <Field label="ZIP" value={addr.zip || ''}
                      onChange={v => upd('addresses', form.addresses.map((a, idx) => idx === i ? { ...a, zip: v } : a))} />
                  </div>
                </div>
                {(form.addresses || []).length > 1 && (
                  <button onClick={() => upd('addresses', form.addresses.filter((_, idx) => idx !== i))}
                    style={{ marginTop: 6, fontSize: 11, color: 'var(--red-400)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>✕ Remove</button>
                )}
              </div>
            ))}
          </div>

          {/* Point of Contact */}
          <div className="card">
            <h3 className="card-title">Point of Contact</h3>
            <div className="form-grid">
              <Field label="Name" value={form.poc_name || ''} onChange={v => upd('poc_name', v)} />
              <Field label="Title" value={form.poc_title || ''} onChange={v => upd('poc_title', v)} />
              <Field label="Phone" value={form.poc_phone || ''} onChange={v => upd('poc_phone', v)} />
              <Field label="Email" value={form.poc_email || ''} onChange={v => upd('poc_email', v)} />
            </div>
          </div>

          {/* Certifications */}
          <div className="card">
            <h3 className="card-title">SB / DVBE Certifications</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>Small Business (SB) Certified</label>
                <select value={form.sb_certified ? 'yes' : 'no'} onChange={e => upd('sb_certified', e.target.value === 'yes')} style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </div>
              {form.sb_certified && <>
                <Field label="SB Cert Number" value={form.sb_cert_number || ''} onChange={v => upd('sb_cert_number', v)} monospace />
                <Field label="SB Expiration Date" value={form.sb_expiration || ''} onChange={v => upd('sb_expiration', v)} type="date" />
              </>}
              <div className="form-field">
                <label>Disabled Veteran Business Enterprise (DVBE) Certified</label>
                <select value={form.dvbe_certified ? 'yes' : 'no'} onChange={e => upd('dvbe_certified', e.target.value === 'yes')} style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </div>
              {form.dvbe_certified && <>
                <Field label="DVBE Cert Number" value={form.dvbe_cert_number || ''} onChange={v => upd('dvbe_cert_number', v)} monospace />
                <Field label="DVBE Expiration Date" value={form.dvbe_expiration || ''} onChange={v => upd('dvbe_expiration', v)} type="date" />
              </>}
            </div>
          </div>

          {/* Registrations & IDs */}
          <div className="card">
            <h3 className="card-title">Registrations & Platform IDs</h3>
            <p style={{ fontSize: 12, color: 'var(--slate-400)', marginBottom: 14 }}>These IDs are stored now and will be used for future FI$Cal and CalEProcure integration.</p>
            <div className="form-grid">
              <div className="form-field">
                <label>SAM.gov Registered</label>
                <select value={form.sam_registered ? 'yes' : 'no'} onChange={e => upd('sam_registered', e.target.value === 'yes')} style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </div>
              {form.sam_registered && <Field label="SAM.gov Expiration" value={form.sam_expiration || ''} onChange={v => upd('sam_expiration', v)} type="date" />}
              <Field label="CalEProcure Vendor ID" value={form.caleprocure_id || ''} onChange={v => upd('caleprocure_id', v)} monospace placeholder="Future integration" />
              <Field label="FI$Cal Vendor ID" value={form.fiscal_id || ''} onChange={v => upd('fiscal_id', v)} monospace placeholder="Future integration" />
            </div>

            {/* NAICS Codes */}
            <div style={{ marginTop: 14 }}>
              <SectionLabel>NAICS Codes</SectionLabel>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input value={naicInput} onChange={e => setNaicInput(e.target.value)} placeholder="e.g., 541330"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && naicInput.trim()) {
                      upd('naics_codes', [...(form.naics_codes || []), naicInput.trim()]);
                      setNaicInput('');
                    }
                  }}
                  style={{ flex: 1, padding: '7px 10px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 13, fontFamily: 'inherit' }} />
                <button className="btn btn-ghost btn-sm" onClick={() => { if (naicInput.trim()) { upd('naics_codes', [...(form.naics_codes || []), naicInput.trim()]); setNaicInput(''); } }}>Add</button>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(form.naics_codes || []).map((code, i) => (
                  <span key={i} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 99, background: 'var(--navy-50)', color: 'var(--navy-700)', border: '1px solid var(--navy-200)', display: 'flex', alignItems: 'center', gap: 5 }}>
                    {code}
                    <button onClick={() => upd('naics_codes', form.naics_codes.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--slate-400)', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="card">
            <h3 className="card-title">Notes</h3>
            <Field label="" value={form.notes || ''} onChange={v => upd('notes', v)} rows={3} placeholder="Agency-specific notes about this vendor…" className="span-full" />
          </div>

          {/* Documents */}
          {!isNew && selected?.id ? (
            <div className="card">
              <h3 className="card-title">Documents</h3>
              <p style={{ fontSize: 12.5, color: 'var(--slate-500)', marginBottom: 14 }}>
                Upload compliance documents. Penny will automatically extract expiration dates, policy numbers, and coverage amounts.
              </p>
              <VendorDocUploader
                vendorId={selected.id}
                docs={form.docs || {}}
                docMeta={form.doc_metadata || {}}
                onUpdate={docs => upd('docs', docs)}
                onMetaUpdate={meta => upd('doc_metadata', meta)}
                onProfileUpdate={updates => setForm(p => ({ ...p, ...updates }))}
                currentProfile={form}
                apiKey={apiKey}
                callAI={callAI}
                toast={toast}
              />
            </div>
          ) : isNew ? (
            <div style={{ padding: '10px 14px', background: 'var(--blue-50)', border: '1px solid var(--blue-100)', borderRadius: 'var(--r-md)', fontSize: 12.5, color: 'var(--blue-700)' }}>
              💡 Save the vendor first, then you can upload documents.
            </div>
          ) : null}

          {/* Sticky save bar */}
          <div style={{
            position: 'sticky', bottom: 0, zIndex: 10,
            background: 'linear-gradient(to top, var(--white) 85%, transparent)',
            paddingTop: 16, paddingBottom: 12, marginTop: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderRadius: 'var(--r-lg)', border: '1.5px solid ' + (dirty ? 'var(--gold-400)' : 'var(--slate-200)'), background: dirty ? 'var(--gold-50)' : 'var(--slate-50)', boxShadow: '0 -2px 12px rgba(0,0,0,.06)' }}>
              <button className="btn btn-gold" disabled={saving || (!dirty && !isNew)} onClick={save} style={{ minWidth: 130 }}>
                {saving ? <><div className="spin spin-sm" /> Saving…</> : isNew ? '+ Add to Library' : dirty ? '↑ Save Changes' : '✓ Saved'}
              </button>
              <button className="btn btn-ghost" onClick={() => { setSelected(null); setIsNew(false); setDirty(false); }}>Cancel</button>
              {!isNew && selected?.id && (
                <button className="btn btn-ghost" style={{ color: 'var(--red-400)', marginLeft: 'auto' }} onClick={() => deactivate(selected.id)}>Deactivate</button>
              )}
              {lastSaved && !dirty && (
                <span style={{ fontSize: 11, color: 'var(--slate-400)', marginLeft: 'auto' }}>
                  Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {dirty && !saving && (
                <span style={{ fontSize: 11, color: 'var(--gold-600)', fontWeight: 600 }}>Unsaved changes</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
